/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: How to make TikTok videos with AI - 4-step workflow
 */
import Image from 'next/image';
import React from 'react';

interface StepCardProps {
  index: number;
  title: string;
  description: string;
}

const steps = [
  {
    title: 'Describe Your Idea or Paste a Link',
    description:
      'Enter a short script, paste a product URL, or upload your existing clips. Tell the AI what style you want — funny, aesthetic, storytelling, UGC, or promo.',
  },
  {
    title: 'Access an AI Tool',
    description:
      'The AI analyzes your input and understands your goal, content type, and tone. No editing skills or templates required to get started.',
  },
  {
    title: 'Generate a TikTok Video with AI',
    description:
      'Automatically turn your idea into a TikTok-ready video. Scenes, pacing, visuals, and structure are generated for short-form performance. Fine-tune visuals, text, timing, and layout to match your brand or personal style.',
  },
  {
    title: 'Export & Share',
    description:
      'Export your video in the right format for TikTok. Post instantly and start testing content faster.',
  },
];

const StepCard: React.FC<StepCardProps> = ({ index, title, description }) => {
  return (
    <div className="flex gap-4 rounded-2xl p-4 pad:p-5">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#6366F1] text-white flex items-center justify-center font-semibold text-sm">
        {index}
      </div>
      <div>
        <h3 className="text-base pad:text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-xs pad:text-sm text-gray-600 mt-1">{description}</p>
      </div>
    </div>
  );
};

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-8 text-center">
          How to Make TikTok Videos with AI
        </h2>

        <div className="max-w-[1010px] mx-auto flex flex-col sm:flex-row items-center gap-8 pad:gap-10 sm:gap-12">
          {/* Visual */}
          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden">
              <Image
                src="https://assets.falcocut.ai/tools/tiktok-video-generator/how-to.webp"
                alt="How to make TikTok videos with FalcoCut AI"
                width={440}
                height={520}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
          {/* Steps */}
          <div className="flex-1 w-full">
            <div className="space-y-4">
              {steps.map((step, index) => (
                <StepCard
                  key={step.title}
                  index={index + 1}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
